/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useReducer } from "react";
import {
  Checkbox,
  CheckboxGroup,
  InputGroup,
  ResponsiveTableRow,
  Cell,
  Grid,
  Separator,
} from "@fs/zion-ui"
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { usePageView } from "@fs/zion-analytics";
import { DmcResponsiveTable } from '../../../../common/DmcResponsiveTable';
import { isNullOrUndefinedOrEmpty } from "../../../../../util/Utils";
import {
  getPagedJobInputs,
  getPagedJobResults,
} from "../../../../../services/toolsService";
import { DatsJobBaseContext } from "../DatsJobContext";
import ToolsButtons from "../ToolsButtons";
import DmcPaginator from "../../../../common/DmcPaginator";
import { usePageTitle } from "../../../../app/PageTitleContext";

/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-array-index-key */
export default () => {};

export function JobViewResults({ datsJobActions }) {
  usePageView({ page_detail: 'DMConsole:DatsJobResults' })
  const { jobViewState, jobViewActions } = useJobViewResults();
  const { t } = useTranslation();
  usePageTitle(t('pageTitle.tools.datsJobResults'))

  const { datsJobState } = useContext(DatsJobBaseContext);
  useEffect(() => {
    if (datsJobState.jobId) {
      jobViewActions.getPagedJobInputsAndResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobViewState.pagination.index]);

  return (
    <>
      <Separator size="xxs" />
      <ToolsButtons datsJobActions={datsJobActions} />
      <Grid>
        <Cell columns={9}>
          <CheckboxGroup
            orientation="horizontal"
            label={t("tools.filterResults", "Filter Results")}
          >
            <Checkbox
              name="successful"
              label={t("tools.includeSuccessful", "Include Successful")}
              checked={jobViewState.filters.includeSuccessful}
              onChange={(event) =>
                jobViewActions.setIncludeSuccessful(event.target.checked)
              }
            />
            <Checkbox
              name="failures"
              label={t("dats.includeFailures", "Include Failures")}
              checked={jobViewState.filters.includeFailures}
              onChange={(event) =>
                jobViewActions.setIncludeFailures(event.target.checked)
              }
            />
          </CheckboxGroup>
        </Cell>
        <Cell columns={3}>
          {datsJobState.jobData && datsJobState.jobData.workDone > 30 && (
            <InputGroup label="">
              <DmcPaginator
                pageCount={30}
                index={jobViewState.pagination.index}
                setIndex={jobViewActions.setPaginationIndex}
                hideResultsPerPage
                length={datsJobState.jobData.workDone}
              />
            </InputGroup>
          )}
        </Cell>
      </Grid>

      {!isNullOrUndefinedOrEmpty(jobViewState.resultsData) &&
        !isNullOrUndefinedOrEmpty(jobViewState.inputsData) && (
        <DmcResponsiveTable
          totalColumnCount={4}
          atSizeColumns={{
            default: {
              labels: [
                t("id", "ID"),
                t("status.label", "Status"),
                t("dats.input", "Input"),
                t("dats.Response", "Response"),
              ],
              columnWidths: [1, 1, 5, 5],
            },
          }}
        >
          {jobViewState.resultsData.responseItems.map((responseRow) => {
            const indexRow =
              (responseRow.id - 1) % jobViewState.pagination.count; // The id is the index - 1
            const inputRow = jobViewState.inputsData[indexRow];
            return (
              <ResponsiveTableRow
                column1={responseRow.id}
                column2={
                  responseRow?.successful
                    ? t("success", "Success")
                    : t("dats.failure", "Failure")
                }
                column3={inputRow}
                column4={
                  responseRow?.values &&
                  Object.keys(responseRow.values).map((resultKey) => {
                    const label = resultKey.includes('varArg') ? '' : `${resultKey}: `
                    return <div key={v4()}>{label}{responseRow.values[resultKey]}</div>;
                  })
                }
              />
            );
          })}
        </DmcResponsiveTable>
      )}
    </>
  );
}

function useJobViewResults() {
  const { datsJobState } = useContext(DatsJobBaseContext);
  const jobViewResultsInitialState = {
    filters: {
      includeSuccessful: true,
      includeFailures: true,
    },
    pagination: {
      index: 0,
      start: 1,
      count: 30,
      totalItems: null,
    },
    resultsData: null,
    inputsData: null,
  };

  const reducer = (_, currentState) => JSON.parse(JSON.stringify(currentState));
  const [jobViewState, updateJobVireResultsState] = useReducer(
    reducer,
    jobViewResultsInitialState
  );

  const jobViewActions = {
    setIncludeFailures: (includeFailures) => {
      jobViewState.pagination.index = 0;
      jobViewState.filters.includeFailures = includeFailures;
      updateJobVireResultsState(jobViewState);
      return jobViewActions.getPagedJobResults();
    },
    setIncludeSuccessful: (includeSuccessful) => {
      jobViewState.pagination.index = 0;
      jobViewState.filters.includeSuccessful = includeSuccessful;
      updateJobVireResultsState(jobViewState);
      return jobViewActions.getPagedJobResults();
    },
    setResultsData: (resultsData) => {
      jobViewState.resultsData = resultsData;
      updateJobVireResultsState(jobViewState);
    },
    setPaginationIndex: (index) => {
      jobViewState.pagination.index = index;
      // eslint-disable-next-line no-extra-parens
      jobViewState.pagination.start =
        jobViewState.pagination.count * jobViewState.pagination.index + 1;
      updateJobVireResultsState(jobViewState);
    },
    setPaginationCount: (count) => {
      jobViewState.pagination.count = count;
      updateJobVireResultsState(jobViewState);
    },
    setPaginationTotalItems: (totalItems) => {
      jobViewState.pagination.totalItems = totalItems;
      updateJobVireResultsState(jobViewState);
    },
    getPagedJobResults() {
      return getPagedJobResults(
        datsJobState.jobId,
        jobViewState.pagination.start,
        jobViewState.pagination.count,
        jobViewState.filters.includeSuccessful,
        jobViewState.filters.includeFailures
      ).then((resultsData) => {
        jobViewState.resultsData = resultsData;
        updateJobVireResultsState(jobViewState);
        return Promise.resolve();
      });
    },
    getPagedJobInputs() {
      return getPagedJobInputs(
        datsJobState.jobId,
        jobViewState.pagination.start,
        jobViewState.pagination.count
      ).then((inputsData) => {
        jobViewState.inputsData = inputsData;
        updateJobVireResultsState(jobViewState);
        return Promise.resolve();
      });
    },
    getPagedJobInputsAndResults: async () => {
      await jobViewActions.getPagedJobInputs();
      await jobViewActions.getPagedJobResults();
      return Promise.resolve();
    },
  };

  return { jobViewState, jobViewActions };
}

/* eslint-enable no-underscore-dangle */
/* eslint-enable react/no-array-index-key */
